import React, { useState } from 'react';
import { Box, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { CheckCircle, WhatsApp } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useToast } from '../../common/ToastProvider';
import { buildReceiptPdfBlob, buildReturnReceiptPdfBlob } from '../receipt/downloadReceiptPdf';
import { useSendWhatsappBillMutation, useUpdateWhatsappOptInMutation } from '../../../services/whatsappApi';

const DEFAULT_SEND_LIMIT = 3;

/* ── WhatsApp billing section (checkout success screen and Order History) ──
   Also sends a return's credit note: pass the return with document_kind
   'credit_note' and its return receipt; it goes through the same template.
   source: where it is shown — 'checkout' or 'order_history'. Each place may
   send a bill order.whatsapp_send_limit times (backend-enforced); the button
   turns into Resend after the first send and disables at the limit. */
export const WhatsAppSection = ({ order, receipt, customerWhatsappOptIn, source = 'checkout' }) => {
  const showToast = useToast();
  const { organization } = useSelector((state) => state.auth);
  const [sendBill]       = useSendWhatsappBillMutation();
  const [updateOptIn]    = useUpdateWhatsappOptInMutation();

  const limit = order?.whatsapp_send_limit ?? DEFAULT_SEND_LIMIT;
  const [localOptIn, setLocalOptIn] = useState(customerWhatsappOptIn ?? null);
  const [sendCount, setSendCount]   = useState(
    order?.[`whatsapp_${source}_send_count`] ?? (source === 'checkout' && order?.whatsapp_bill_sent_at ? 1 : 0),
  );
  const [showConsent, setShowConsent] = useState(false);
  const [isSending, setIsSending]   = useState(false);

  const available    = organization?.whatsapp_billing_available;
  const optInEnabled = organization?.whatsapp_opt_in_enabled;
  const hasCustomer  = !!order?.customer_id;
  const isCreditNote = order?.document_kind === 'credit_note';
  const documentLabel = isCreditNote ? 'Credit Note' : 'Bill';

  if (!available || !hasCustomer) return null;

  const doSend = async () => {
    if (!receipt) {
      showToast('Receipt is still loading — try again in a moment.', 'error');
      return;
    }
    setIsSending(true);
    try {
      const isVoucherSale = order.document_kind === 'gift_voucher_sale';
      const blob = isCreditNote ? await buildReturnReceiptPdfBlob(receipt) : await buildReceiptPdfBlob(receipt);
      const documentNumber = isCreditNote ? (order.credit_note_number || order.return_number) : order.order_number;
      const target = isCreditNote ? { orderReturnId: order.id }
        : isVoucherSale ? { giftVoucherSaleId: order.id } : { orderId: order.id };
      const res  = await sendBill({
        ...target,
        source,
        billBlob: blob,
        filename: `${isCreditNote ? 'CreditNote' : isVoucherSale ? 'Receipt' : 'Invoice'}-${String(documentNumber).replace(/\//g, '-')}.pdf`,
      }).unwrap();
      setSendCount((c) => res.data?.send_count ?? c + 1);
      showToast(`${documentLabel} sent${sendCount > 0 ? ' again' : ''} on WhatsApp!`, 'success');
    } catch (err) {
      // Sent elsewhere (another tab or terminal) up to the limit meanwhile.
      if (/times on WhatsApp/.test(err.data?.error || '')) setSendCount(limit);
      showToast(err.data?.error || 'Failed to send on WhatsApp.', 'error');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendClick = () => {
    if (!optInEnabled || localOptIn === true) {
      doSend();
    } else if (localOptIn === null || localOptIn === undefined) {
      setShowConsent(true);
    }
  };

  const handleConsentYes = async () => {
    setShowConsent(false);
    setLocalOptIn(true);
    updateOptIn({ customerId: order.customer_id, optedIn: true }).catch(() => {});
    await doSend();
  };

  const handleConsentNo = () => {
    setShowConsent(false);
    setLocalOptIn(false);
    updateOptIn({ customerId: order.customer_id, optedIn: false }).catch(() => {});
  };

  const handleAskAgain = () => {
    setLocalOptIn(null);
    setShowConsent(true);
  };

  if (sendCount > 0) {
    const limitReached = sendCount >= limit;
    return (
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.25, pt: 0.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          bgcolor: '#F0FDF4', border: '1px solid #BBF7D0',
          borderRadius: '8px', pl: 1.5, pr: 0.75, py: 0.5, minHeight: 38,
        }}>
          <CheckCircle sx={{ fontSize: 15, color: '#16A34A', flexShrink: 0 }} />
          <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#15803D', minWidth: 0 }}>
            Sent on WhatsApp
          </Typography>
          <Box component="span" title={`Sent ${sendCount} of ${limit} times from here`}
            sx={{ flexShrink: 0, px: 0.6, borderRadius: '5px', bgcolor: '#DCFCE7', color: '#15803D', fontSize: '0.66rem', fontWeight: 800, lineHeight: '18px' }}>
            {sendCount}/{limit}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            onClick={handleSendClick}
            disabled={isSending || limitReached}
            startIcon={isSending ? <CircularProgress size={12} sx={{ color: '#25D366' }} /> : limitReached ? null : <WhatsApp sx={{ fontSize: '0.9rem !important' }} />}
            sx={{
              flexShrink: 0, whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 800, fontSize: '0.72rem', borderRadius: '7px', px: 1.25, py: 0.4,
              color: '#fff', bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe59' },
              '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
            }}
          >
            {isSending ? 'Sending…' : limitReached ? 'Limit reached' : 'Resend'}
          </Button>
        </Box>
      </Box>
    );
  }

  if (optInEnabled && localOptIn === false) {
    return (
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.25, pt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsApp sx={{ fontSize: 14, color: '#94A3B8' }} />
        <Typography sx={{ fontSize: '0.73rem', color: '#94A3B8' }}>Customer opted out</Typography>
        <Button
          size="small"
          onClick={handleAskAgain}
          sx={{ ml: 'auto', fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, color: '#4F46E5', p: 0, minWidth: 0 }}
        >
          Ask again?
        </Button>
      </Box>
    );
  }

  if (showConsent) {
    return (
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.25, pt: 0.5 }}>
        <Typography sx={{ fontSize: '0.74rem', color: '#334155', fontWeight: 600, mb: 0.85 }}>
          Does the customer consent to receive their {documentLabel.toLowerCase()} on WhatsApp?
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" disableElevation fullWidth
            onClick={handleConsentYes}
            disabled={isSending}
            sx={{ py: 0.75, borderRadius: '7px', fontWeight: 800, fontSize: '0.78rem', textTransform: 'none', bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe59' } }}
          >
            {isSending ? 'Sending…' : 'Yes, Send'}
          </Button>
          <Button
            variant="outlined" disableElevation fullWidth
            onClick={handleConsentNo}
            sx={{ py: 0.75, borderRadius: '7px', fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B', '&:hover': { bgcolor: '#F8FAFC' } }}
          >
            No
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.25, pt: 0.5 }}>
      <Button
        variant="outlined" fullWidth
        startIcon={isSending ? <CircularProgress size={14} sx={{ color: '#25D366' }} /> : <WhatsApp sx={{ fontSize: '1rem !important' }} />}
        onClick={handleSendClick}
        disabled={isSending}
        sx={{
          py: { xs: 0.9, sm: 1 },
          borderRadius: '8px',
          fontWeight: 700,
          fontSize: { xs: '0.78rem', sm: '0.82rem' },
          textTransform: 'none',
          borderColor: '#25D366',
          color: '#25D366',
          '&:hover': { bgcolor: '#F0FFF4', borderColor: '#1ebe59' },
          '&.Mui-disabled': { borderColor: '#CBD5E1', color: '#CBD5E1' },
        }}
      >
        {isSending ? 'Sending…' : `Send ${documentLabel} on WhatsApp`}
      </Button>
    </Box>
  );
};

export default WhatsAppSection;
